import React from 'react';
import cn from 'classnames';
import { withFormik, FormikProps, WithFormikConfig } from 'formik';
import * as yup from 'yup';
import { Input, Button2, Select3 } from '@el8/vital';
import { Box } from 'components';
import ButtonsBlock from 'views/booking/common/ButtonsBlock/ButtonsBlock';
import { isValidDateOfBirth, phoneRegexp } from 'utils/validators';
import { formatDob, formatPhone } from 'utils/format';
import { PatientSex, PatientChartInfo } from 'types';
import styles from './PatientDetailsForm.less';

type OwnProps = {
  patientInfo: PatientChartInfo | null;
  setPatientDetails: (values: PatientChartInfo) => void;
};

type Props = OwnProps & FormikProps<PatientChartInfo>;

const getValidationSchema = (
  bookViaEmailOrPhoneEnabled: boolean,
): yup.ObjectSchema<Partial<PatientChartInfo>> => {
  return yup.object().shape<Partial<PatientChartInfo>>(
    {
      dob: yup
        .string()
        .required('Required')
        .test({
          name: 'is-dob',
          message: 'DOB should make sense',
          test(value) {
            return isValidDateOfBirth(this, value);
          },
        }),
      firstName: yup.string().required('Required'),
      lastName: yup.string().required('Required'),
      phone: bookViaEmailOrPhoneEnabled
        ? yup
            .string()
            .matches(phoneRegexp, 'Invalid phone number. Please use XXX-XXX-XXXX format')
            .when('email', {
              is: (val: string) => Boolean(val?.trim()),
              then: (schema: yup.StringSchema) => schema.nullable(true),
              otherwise: (schema: yup.StringSchema) =>
                schema.required('At least one of phone or email is required'),
            })
        : yup
            .string()
            .required('Required')
            .matches(phoneRegexp, 'Invalid phone number. Please use XXX-XXX-XXXX format'),
      email: bookViaEmailOrPhoneEnabled
        ? yup
            .string()
            .email('Invalid email address')
            .when('phone', {
              is: (val: string) => Boolean(val?.trim()),
              then: (schema: yup.StringSchema) => schema.nullable(true),
              otherwise: (schema: yup.StringSchema) =>
                schema.required('At least one of phone or email must be set'),
            })
        : yup.string().required('Required').email('Invalid email address'),
      sex: yup.mixed().oneOf(Object.values(PatientSex)).required('Required'),
    },
    [['email', 'phone']], // nested fields
  );
};

const patientDetailsValidationSchema = getValidationSchema(
  window.el8Globals.FEATURES.BookViaEmailOrPhone,
);

const formikConfig: WithFormikConfig<OwnProps, PatientChartInfo> = {
  handleSubmit: (values, { props }): void => {
    props.setPatientDetails(values);
  },
  mapPropsToValues: (props): PatientChartInfo => ({
    firstName: '',
    lastName: '',
    dob: '',
    email: '',
    phone: '',
    sex: undefined,
    ...props.patientInfo,
  }),
  validateOnMount: true, // Ensure validation runs on mount
  validationSchema: patientDetailsValidationSchema,
  enableReinitialize: true,
};

const PatientDetailsForm: React.FC<Props> = ({
  errors,
  handleChange,
  handleBlur,
  handleSubmit,
  isSubmitting,
  isValid,
  setFieldValue,
  setFieldTouched,
  touched,
  values,
}) => (
  <form onSubmit={handleSubmit}>
    <Box direction="column">
      <Box align="top">
        <Input
          name="firstName"
          error={touched.firstName && errors.firstName}
          value={values.firstName}
          label="First Name"
          onChange={handleChange}
          onBlur={handleBlur}
          className={cn(styles.field, styles.withRightMargin)}
        />
        <Input
          name="lastName"
          error={touched.lastName && errors.lastName}
          value={values.lastName}
          label="Last Name"
          onChange={handleChange}
          onBlur={handleBlur}
          className={styles.field}
        />
      </Box>
      <Box align="top">
        <Input
          name="dob"
          error={touched.dob && errors.dob}
          value={formatDob(values.dob)}
          label="Date of Birth"
          placeholder="MM/DD/YYYY"
          /**
           * A little bit of hack here. In order to show a special keypad on mobile phone we need to
           * set an appropriate text input type, i.e. "date", "number", "tel". Problem with "date"
           * and "number" is that mobile safari and chrome (mobile and desktop) break styling of our
           * text input field and make page look bad. The only option that we have is a "tel" type
           * which semantically is not correct, but does the thing.
           */
          type="tel"
          onChange={handleChange}
          onBlur={handleBlur}
          className={cn(styles.field, styles.withRightMargin)}
        />
        <Select3
          className={styles.field}
          name="sex"
          label="Sex at Birth"
          error={touched.sex && errors.sex}
          onBlur={(): void => setFieldTouched('sex')}
          onChange={(value: string): void => {
            setFieldValue('sex', value);
          }}
          value={values.sex}
          items={[
            { value: PatientSex.female, label: 'Female' },
            { value: PatientSex.male, label: 'Male' },
            { value: PatientSex.intersexAndOrOther, label: 'Intersex/Other' },
            { value: PatientSex.unspecified, label: 'Unspecified' },
          ]}
        />
      </Box>
      <Input
        name="email"
        error={touched.email && errors.email}
        value={values.email}
        label="Email"
        placeholder="john@example.com"
        type="email"
        onChange={handleChange}
        onBlur={handleBlur}
        className={styles.field}
      />
      <Input
        name="phone"
        error={touched.phone && errors.phone}
        value={formatPhone(values.phone)}
        label="Phone Number"
        placeholder="XXX-XXX-XXXX"
        type="tel"
        onChange={handleChange}
        onBlur={handleBlur}
        className={styles.field}
      />
      <ButtonsBlock>
        <Button2
          busy={isSubmitting}
          className={styles.nextButton}
          disabled={!isValid}
          kind="primary"
          label="Continue"
          type="submit"
        />
      </ButtonsBlock>
    </Box>
  </form>
);

export default withFormik(formikConfig)(PatientDetailsForm);
